import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { flushSync } from "react-dom";
import { Box, Typography, useTheme } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  updateLoading,
  updateLoadingMsg,
  addActiveRequests,
} from "../../Redux/Reducers/uiSlice";
import { findBrokerAbb } from "../../component/Methods";
import { sendTradesToPats } from "../../utils/restAPI";
import { useTradeBlotterState } from "../../hooks/useOptimizedState";
import TradeBlotterTable from "./TradeBlotterTable";
import TradeBlotterToolbar from "./components/TradeBlotterToolbar";
import TradeBlotterEmptyState from "./components/TradeBlotterEmptyState";
import NewOrderDialog from "./Popup/NewOrderDialog";
import BulkEdit from "./Popup/BulkEdit";
import BulkDelete from "./Popup/BulkDelete";
import DeletePopUp from "./Popup/DeletePopUp";
import EditTradeDialog from "./Popup/EditTradeDialog";
import ManageFillsDialog from "./Popup/ManageFillsDialog";
import SummaryDialog from "./Popup/SummaryDialog";
import RouteDialog from "./Popup/RouteDialog";
import { useTradeBlotterActions } from "./hooks/useTradeBlotterActions";
import { transformTradesForGrid } from "./tradeBlotterTransform";
import {
  GET_SSANDC_FILLS_REQUEST,
  getStartOfLocalDay,
  formatLocalDateInputValue,
  routerDisplayIncludesSsandc,
  isSsandcRoutedSelectionItem,
  isSsandcRoutedOnlySelectionItem,
  getEligibleTradeIdsFromSelection,
  filterTradeRowsBySelectedDate,
  filterTradeRowsByDropdownFilters,
  collectUniqueTradeFieldValues,
  getTradeBlotterTickerOptions,
  flattenTradeRowsForGrid,
  prepareTradeBlotterGridData,
  isCommissionEligibleTrade,
} from "./tradeBlotterHelpers";

const TradeBlotter = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const dataGridRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEntity, setSelectedEntity] = useState("");
  const [selectedStrategy, setSelectedStrategy] = useState("");
  const [selectedTicker, setSelectedTicker] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => getStartOfLocalDay());
  const [newOrderDialogOpen, setNewOrderDialogOpen] = useState(false);
  const [selectedTrades, setSelectedTrades] = useState([]);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [isBulkArchive, setIsBulkArchive] = useState(false);
  const [deletePopUpOpen, setDeletePopUpOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTradeForEdit, setSelectedTradeForEdit] = useState(null);
  const [manageFillsDialogOpen, setManageFillsDialogOpen] = useState(false);
  const [selectedTradeForFills, setSelectedTradeForFills] = useState(null);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [routeDialogOpen, setRouteDialogOpen] = useState(false);
  const [selectedRouterOption, setSelectedRouterOption] = useState("");
  const [viewType, setViewType] = useState("non-deleted");
  const [flatChildView, setFlatChildView] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [sendingPats, setSendingPats] = useState(false);
  const [gridMounted, setGridMounted] = useState(true);

  useEffect(() => {
    const onBeforeNavigate = () => {
      flushSync(() => {
        setGridMounted(false);
      });
    };
    window.addEventListener("va:before-navigate", onBeforeNavigate);
    return () => {
      window.removeEventListener("va:before-navigate", onBeforeNavigate);
    };
  }, []);

  const { trades, deletes, brokers, strategies, funds, custodians, routes, livePrices } =
    useTradeBlotterState();
  const { userInfo, apiInfo } = useSelector((state) => state?.users);
  const isUpdateFillsLoading = useSelector((state) =>
    (state.ui?.activeRequests || []).includes(GET_SSANDC_FILLS_REQUEST)
  );

  const startLoadingRequest = useCallback(
    (requestKey, message) => {
      dispatch(addActiveRequests(requestKey));
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg(message));
    },
    [dispatch]
  );

  const clearDataGridSelection = useCallback(() => {
    dataGridRef.current?.clearSelection?.();
  }, []);

  const handleEditTrade = useCallback((tradeData) => {
    setSelectedTradeForEdit(tradeData);
    setEditDialogOpen(true);
  }, []);

  const handleManageFills = useCallback((tradeData) => {
    setSelectedTradeForFills(tradeData);
    setManageFillsDialogOpen(true);
  }, []);

  const transformContext = useMemo(
    () => ({
      funds,
      strategies,
      custodians,
      brokers,
      routes,
      livePrices,
      onEditClick: handleEditTrade,
      onManageFillsClick: handleManageFills,
    }),
    [
      funds,
      strategies,
      custodians,
      brokers,
      routes,
      livePrices,
      handleEditTrade,
      handleManageFills,
    ]
  );

  const transformedData = useMemo(() => {
    const dataSource = viewType === "deleted" ? deletes : trades;
    return transformTradesForGrid(dataSource, transformContext);
  }, [trades, deletes, viewType, transformContext]);

  const dateFilteredData = useMemo(
    () => filterTradeRowsBySelectedDate(transformedData, selectedDate),
    [transformedData, selectedDate]
  );

  const dropdownOptionRows = useMemo(
    () =>
      flatChildView
        ? flattenTradeRowsForGrid(dateFilteredData)
        : dateFilteredData,
    [dateFilteredData, flatChildView]
  );

  const uniqueFunds = useMemo(
    () => collectUniqueTradeFieldValues(dropdownOptionRows, "fund"),
    [dropdownOptionRows]
  );

  const uniqueStrategies = useMemo(
    () => collectUniqueTradeFieldValues(dropdownOptionRows, "strategy"),
    [dropdownOptionRows]
  );

  const uniqueTickers = useMemo(
    () =>
      getTradeBlotterTickerOptions(dateFilteredData, {
        searchTerm,
        selectedEntity,
        selectedStrategy,
        flatChildView,
      }),
    [dateFilteredData, selectedEntity, selectedStrategy, searchTerm, flatChildView]
  );

  const dropdownFilters = useMemo(
    () => ({ selectedEntity, selectedStrategy, selectedTicker }),
    [selectedEntity, selectedStrategy, selectedTicker]
  );

  const searchFilteredData = useMemo(
    () => prepareTradeBlotterGridData(dateFilteredData, searchTerm, false, dropdownFilters),
    [dateFilteredData, searchTerm, dropdownFilters]
  );

  const tableData = useMemo(
    () => prepareTradeBlotterGridData(dateFilteredData, searchTerm, flatChildView, dropdownFilters),
    [dateFilteredData, searchTerm, flatChildView, dropdownFilters]
  );

  const {
    handleUpdateFills,
    handleRouteSubmit,
    handleAllocationSubmit,
    handleCancelTradesSubmit,
    handleSubmitFills,
    updateCommissionForEligibleTrades,
    handleArchiveSelected,
  } = useTradeBlotterActions({
    selectedTrades,
    searchFilteredData,
    trades,
    selectedDate,
    brokers,
    routes,
    apiInfo,
    userInfo,
    startLoadingRequest,
    setRouteDialogOpen,
  });

  useEffect(() => {
    setSelectedTrades([]);
    dataGridRef.current?.clearSelection?.();
  }, [selectedDate, viewType]);

  useEffect(() => {
    setSelectedEntity("");
    setSelectedStrategy("");
    setSelectedTicker("");
  }, [selectedDate, viewType]);

  useEffect(() => {
    setSelectedTrades([]);
    dataGridRef.current?.clearSelection?.();
  }, [flatChildView, searchTerm, selectedEntity, selectedStrategy, selectedTicker]);

  useEffect(() => {
    const source = viewType === "deleted" ? deletes || [] : trades || [];
    setSelectedTrades((prev) => {
      if (!prev?.length) return prev;
      let changed = false;
      const next = prev.map((item) => {
        if (item?.tradeId == null) return item;
        const fresh = source.find(
          (t) =>
            t?.tradeId != null &&
            (t.tradeId === item.tradeId || String(t.tradeId) === String(item.tradeId))
        );
        if (!fresh || fresh === item.tradeObject) return item;
        changed = true;
        return { ...item, tradeObject: fresh };
      });
      return changed ? next : prev;
    });
  }, [trades, deletes, viewType]);

  const hasSsandcFillsRefreshTargets = useMemo(
    () => tableData.some((row) => routerDisplayIncludesSsandc(row.routerDisplay)),
    [tableData]
  );

  const hasCommissionEligibleTradesForDate = useMemo(
    () =>
      Array.isArray(trades) &&
      trades.length > 0 &&
      trades.some((item) => isCommissionEligibleTrade(item, selectedDate, brokers)),
    [trades, selectedDate, brokers]
  );

  const formattedSelectedDate = useMemo(
    () =>
      selectedDate.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    [selectedDate]
  );

  const shortFormattedDate = useMemo(
    () =>
      selectedDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
    [selectedDate]
  );

  const dateInputValue = useMemo(
    () => formatLocalDateInputValue(selectedDate),
    [selectedDate]
  );

  const allocationEligibleTradeIds = useMemo(
    () =>
      getEligibleTradeIdsFromSelection(selectedTrades, routes, isSsandcRoutedSelectionItem),
    [selectedTrades, routes]
  );

  const cancelEligibleTradeIds = useMemo(
    () =>
      getEligibleTradeIdsFromSelection(selectedTrades, routes, isSsandcRoutedOnlySelectionItem),
    [selectedTrades, routes]
  );

  const patsEligibleTradeIds = useMemo(() => {
    if (!Array.isArray(selectedTrades) || selectedTrades.length === 0) return [];

    return selectedTrades
      .filter((trade) => {
        const orders = trade?.tradeObject?.orders;
        const lastOrder =
          orders && orders.length > 0 ? orders[orders.length - 1] : null;
        if (!lastOrder) return false;

        const isPrivateInvestment = lastOrder.ticker?.investment === 13;
        const brokerAbb = findBrokerAbb(lastOrder.broker, brokers);
        const isPatsBroker = brokerAbb?.toLowerCase() === "pats";

        return isPrivateInvestment && isPatsBroker;
      })
      .map((trade) => String(trade.tradeId));
  }, [selectedTrades, brokers]);

  const hasOnlyPatsEligibleSelection =
    selectedTrades.length > 0 &&
    patsEligibleTradeIds.length === selectedTrades.length;
  const canShowSendPatsButton = selectedTrades.length > 0;
  const canSendPats = hasOnlyPatsEligibleSelection && !sendingPats;

  const handleSendPats = useCallback(async () => {
    if (!hasOnlyPatsEligibleSelection) {
      toast.info("Select only Private trades routed to PATS to enable this action.");
      return;
    }

    setSendingPats(true);
    try {
      const response = await sendTradesToPats(patsEligibleTradeIds, true);
      if (response.success) {
        toast.success(
          `${patsEligibleTradeIds.length} trade${patsEligibleTradeIds.length === 1 ? "" : "s"} sent to PATS.`
        );
      } else {
        toast.error(response.message || "Failed to send trades to PATS.");
      }
    } finally {
      setSendingPats(false);
    }
  }, [hasOnlyPatsEligibleSelection, patsEligibleTradeIds]);

  const handleTradeSelectionChange = useCallback(
    (newSelection) => {
      const selectedTradeObjects = [];
      const seenTradeIds = new Set();

      newSelection.forEach((rowId) => {
        const row =
          tableData.find((trade) => String(trade.id) === String(rowId)) ||
          dateFilteredData.find((trade) => String(trade.id) === String(rowId));
        if (!row) return;

        const resolvedRow = row.parentTradeId
          ? dateFilteredData.find(
              (trade) => String(trade.id) === String(row.parentTradeId)
            ) || row
          : row;

        if (seenTradeIds.has(resolvedRow.tradeId)) return;
        seenTradeIds.add(resolvedRow.tradeId);
        selectedTradeObjects.push(resolvedRow);
      });

      setSelectedTrades(
        selectedTradeObjects
          .map((transformedTrade) => {
            const originalTrade = trades.find(
              (trade) => trade.tradeId === transformedTrade.tradeId
            );
            return {
              tradeObject: originalTrade,
              tradeId: transformedTrade.tradeId,
              filledPercent: transformedTrade.filledPercent,
            };
          })
          .filter((item) => item.tradeObject)
      );
    },
    [tableData, dateFilteredData, trades]
  );

  const handleBulkEditClose = () => {
    setBulkEditOpen(false);
    clearDataGridSelection();
  };

  const handleBulkDeleteClose = () => {
    setBulkDeleteOpen(false);
    setIsBulkArchive(false);
    clearDataGridSelection();
  };

  const handleRouteSelect = () => {
    if (!selectedTrades?.length) {
      toast.error("Please select at least one trade to route");
      return;
    }
    setRouteDialogOpen(true);
  };

  const handleArchiveClick = () => {
    if (handleArchiveSelected()) {
      setIsBulkArchive(true);
      setBulkDeleteOpen(true);
    }
  };

  const isDeletedView = viewType === "deleted";

  return (
    <>
      <Helmet>
        <title>{isDeletedView ? "Deleted Trades" : "Trade Blotter"} | Vantage Alpha</title>
      </Helmet>

      <Box
        sx={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.background.default,
        }}
      >
          <TradeBlotterToolbar
            theme={theme}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedEntity={selectedEntity}
            onEntityChange={setSelectedEntity}
            uniqueFunds={uniqueFunds}
            selectedStrategy={selectedStrategy}
            onStrategyChange={setSelectedStrategy}
            uniqueStrategies={uniqueStrategies}
            selectedTicker={selectedTicker}
            onTickerChange={setSelectedTicker}
            uniqueTickers={uniqueTickers}
            viewType={viewType}
            onViewTypeChange={setViewType}
            tableDataLength={tableData.length}
            onSelectedDateChange={setSelectedDate}
            dateInputValue={dateInputValue}
            onTodayClick={() => setSelectedDate(getStartOfLocalDay())}
            dataGridRef={dataGridRef}
            hasActiveFilters={hasActiveFilters}
            flatChildView={flatChildView}
            onFlatChildViewChange={setFlatChildView}
            isUpdateFillsLoading={isUpdateFillsLoading}
            hasSsandcFillsRefreshTargets={hasSsandcFillsRefreshTargets}
            onUpdateFills={handleUpdateFills}
            onViewSummary={() => setSummaryDialogOpen(true)}
            onAutoFillCommissions={updateCommissionForEligibleTrades}
            hasCommissionEligibleTradesForDate={hasCommissionEligibleTradesForDate}
            onUpdateSelected={() => setBulkEditOpen(true)}
            onDeleteSelected={() => {
              setIsBulkArchive(false);
              setBulkDeleteOpen(true);
            }}
            onArchiveSelected={handleArchiveClick}
            onRouteSelect={handleRouteSelect}
            onAllocationSubmit={handleAllocationSubmit}
            allocationEligibleCount={allocationEligibleTradeIds.length}
            onCancelTradesSubmit={handleCancelTradesSubmit}
            cancelEligibleCount={cancelEligibleTradeIds.length}
            selectedTradesCount={selectedTrades.length}
            onNewOrder={() => setNewOrderDialogOpen(true)}
            canShowSendPatsButton={canShowSendPatsButton}
            hasOnlyPatsEligibleSelection={hasOnlyPatsEligibleSelection}
            sendingPats={sendingPats}
            canSendPats={canSendPats}
            onSendPats={handleSendPats}
          />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 2,
                flexShrink: 0,
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Showing {isDeletedView ? "deleted" : "active"} trades for:{" "}
                <strong>{formattedSelectedDate}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {tableData.length} {isDeletedView ? "deleted" : ""} trade
                {tableData.length !== 1 ? "s" : ""} found
              </Typography>
            </Box>

            {transformedData.length === 0 ? (
              <TradeBlotterEmptyState
                title={`No ${isDeletedView ? "Deleted" : "Active"} Trade Data Available`}
                description={
                  isDeletedView
                    ? "No deleted trades found for the selected date."
                    : "Trades will appear here once they are added to the system."
                }
              />
            ) : tableData.length === 0 ? (
              <TradeBlotterEmptyState
                title={`No ${isDeletedView ? "Deleted" : "Active"} Trades Found for ${shortFormattedDate}`}
                description={
                  isDeletedView
                    ? "No deleted trades found for this date. Try selecting a different date."
                    : "Try selecting a different date or check if there are trades on other dates."
                }
                actionLabel="Go to Today"
                onAction={() => setSelectedDate(getStartOfLocalDay())}
              />
            ) : !gridMounted ? (
              <Box sx={{ flex: 1, minHeight: 0 }} />
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                <TradeBlotterTable
                  data={tableData}
                  flatChildView={flatChildView}
                  ref={dataGridRef}
                  onFilterStateChange={setHasActiveFilters}
                  onSelectionChange={handleTradeSelectionChange}
                  viewType={viewType}
                  routeList={routes}
                />
              </Box>
            )}
          </Box>
      </Box>

      <NewOrderDialog
        open={newOrderDialogOpen}
        onClose={() => setNewOrderDialogOpen(false)}
        onSubmit={() => {}}
      />
      <BulkEdit
        bulkEdit={bulkEditOpen}
        setBulkEdit={handleBulkEditClose}
        selectionModel={selectedTrades}
      />
      <BulkDelete
        bulkdelete={bulkDeleteOpen}
        setBulkdelete={handleBulkDeleteClose}
        selectionModel={selectedTrades}
        isBulkArchive={isBulkArchive}
        setIsBulkArchive={setIsBulkArchive}
      />
      <DeletePopUp
        openDeletePopup={deletePopUpOpen}
        setDeleteOpenPopup={setDeletePopUpOpen}
        recordForDelete={selectedTrades[0]}
        deleteObj="delete_trade"
        item="trade"
      />
      <EditTradeDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedTradeForEdit(null);
        }}
        tradeData={selectedTradeForEdit}
      />
      <ManageFillsDialog
        open={manageFillsDialogOpen}
        onClose={() => {
          setManageFillsDialogOpen(false);
          setSelectedTradeForFills(null);
        }}
        tradeData={selectedTradeForFills}
        onSubmit={handleSubmitFills}
      />
      <SummaryDialog
        open={summaryDialogOpen}
        onClose={() => setSummaryDialogOpen(false)}
        tradesData={dateFilteredData}
        livePrices={livePrices}
        funds={funds}
      />
      <RouteDialog
        open={routeDialogOpen}
        onClose={() => setRouteDialogOpen(false)}
        onSubmit={handleRouteSubmit}
        selectedRouterOption={selectedRouterOption}
        setSelectedRouterOption={setSelectedRouterOption}
      />
    </>
  );
};

export default TradeBlotter;
